import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState } from 'react';
import './OnboardingWorkflow.css';

/**
 * Section for managing external contacts from other departments
 * These contacts can be assigned to onboarding tasks
 */
const ExternalContactsSection = ({
    contacts = [],
    onAdd,
    onRemove,
    isLoading = false,
    t = (key, fallback) => fallback || key
}) => {
  useUiLanguage();
    const [showAddForm, setShowAddForm] = useState(false);
    const [newContact, setNewContact] = useState({
        name: '',
        email: '',
        department: ''
    });
    const [error, setError] = useState('');

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleAdd = () => {
        setError('');

        if (!newContact.name.trim()) {
            setError(t('externalContacts.validation.enterName', 'Please enter a name'));
            return;
        }

        if (!newContact.email.trim()) {
            setError(t('externalContacts.validation.enterEmail', 'Please enter an email'));
            return;
        }

        if (!validateEmail(newContact.email)) {
            setError(t('externalContacts.validation.validEmail', 'Please enter a valid email address'));
            return;
        }

        if (contacts.some(c => c.email.toLowerCase() === newContact.email.toLowerCase())) {
            setError(t('externalContacts.validation.duplicateEmail', 'This email is already in your contacts'));
            return;
        }

        onAdd({
            ...newContact,
            name: newContact.name.trim(),
            email: newContact.email.trim().toLowerCase(),
            department: newContact.department.trim()
        });

        setNewContact({ name: '', email: '', department: '' });
        setShowAddForm(false);
    };

    const handleCancel = () => {
        setNewContact({ name: '', email: '', department: '' });
        setError('');
        setShowAddForm(false);
    };

    return (
        <div className="external-contacts-section">
            <div className="section-header-contacts">
                <div className="section-title-row">
                    <i className="fa-solid fa-address-book" />
                    <h3>{t('externalContacts.title', 'External Team')}</h3>
                </div>
                <p className="section-description">
                    {t('externalContacts.description', 'Add people from other departments who may handle specific onboarding tasks.')}
                </p>
            </div>

            {/* Contacts List */}
            <div className="contacts-list">
                {isLoading ? (
                    <div className="contacts-loading">
                        <i className="fa-solid fa-spinner fa-spin" />
                        <span>{t('externalContacts.loading', 'Loading contacts...')}</span>
                    </div>
                ) : contacts.length === 0 && !showAddForm ? (
                    <div className="contacts-empty">
                        <i className="fa-solid fa-users-slash" />
                        <span>{t('externalContacts.empty', 'No external contacts added yet')}</span>
                    </div>
                ) : (
                    contacts.map(contact => (
                        <div key={contact.id || contact.email} className="contact-card">
                            <div className="contact-avatar">
                                {contact.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="contact-info">
                                <span className="contact-name">{contact.name}</span>
                                <span className="contact-email">{contact.email}</span>
                                {contact.department && (
                                    <span className="contact-dept">
                                        <i className="fa-solid fa-building" />
                                        {contact.department}
                                    </span>
                                )}
                            </div>
                            <button
                                className="btn-remove-contact"
                                onClick={() => onRemove(contact.id || contact.email)}
                                title={t('tooltips.removeContact', 'Remove contact')}
                            >
                                <i className="fa-solid fa-times" />
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* Add Contact Form */}
            {showAddForm ? (
                <div className="add-contact-form">
                    <div className="form-row-contacts">
                        <div className="form-group-inline">
                            <input
                                type="text"
                                placeholder={t('externalContacts.form.namePlaceholder', 'Full Name *')}
                                value={newContact.name}
                                onChange={e => setNewContact({ ...newContact, name: e.target.value })}
                                autoFocus
                            />
                        </div>
                        <div className="form-group-inline">
                            <input
                                type="email"
                                placeholder={t('externalContacts.form.emailPlaceholder', 'Email Address *')}
                                value={newContact.email}
                                onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                            />
                        </div>
                        <div className="form-group-inline">
                            <select
                                value={newContact.department}
                                onChange={e => setNewContact({ ...newContact, department: e.target.value })}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px',
                                    width: '140px'
                                }}
                            >
                                <option value="">{t('externalContacts.form.departmentPlaceholder', 'Select Dept')}</option>
                                <option value="IT">{t('externalContacts.departments.it', 'IT')}</option>
                                <option value="HR">{t('externalContacts.departments.hr', 'HR')}</option>
                                <option value="Finance">{t('externalContacts.departments.finance', 'Finance')}</option>
                                <option value="Legal">{t('externalContacts.departments.legal', 'Legal')}</option>
                                <option value="Operations">{t('externalContacts.departments.operations', 'Operations')}</option>
                                <option value="Other">{t('externalContacts.departments.other', 'Other')}</option>
                            </select>
                        </div>
                    </div>

                    {error && (
                        <div className="form-error">
                            <i className="fa-solid fa-exclamation-circle" />
                            {uiText(error)}
                        </div>
                    )}

                    <div className="form-actions-contacts">
                        <button type="button" className="btn-cancel" onClick={handleCancel}>
                            {t('buttons.cancel', 'Cancel')}
                        </button>
                        <button type="button" className="btn-add" onClick={handleAdd}>
                            <i className="fa-solid fa-plus" />
                            {t('buttons.addContact', 'Add Contact')}
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    className="btn-add-contact"
                    onClick={() => setShowAddForm(true)}
                >
                    <i className="fa-solid fa-user-plus" />
                    {t('buttons.addExternalContact', 'Add External Contact')}
                </button>
            )}
        </div>
    );
};

export default ExternalContactsSection;
